import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  SvgIcon,
  Typography
} from '@mui/material';
import { Close as CloseIcon } from '@opengov/react-capital-assets';
import React from 'react';

import { getInactiveEntities } from '../../Components/utils';
import { DATA_OG, DATA_TEST, getDynamicDataTestId } from '../../enums';
import { IEntityDetails, OnEntitySelectCallback, PRODUCT_KEY } from '../../types';
import { getApplicationByKey, getEntityDefaultApplication } from '../utils';

import { DesktopEntityAutoCompleteSearch } from './DesktopEntityAutoCompleteSearch';
import { EntityOptionButton } from './EntityOptionButton';
import { navigateToEntityUrl } from './onEntitySelectUtils';

interface IEntitySwitcherDialogProps {
  open: boolean;
  activeEntity: IEntityDetails;
  selectedEntity: IEntityDetails;
  entitledEntities: IEntityDetails[];
  forcedDefaultApplication?: PRODUCT_KEY;
  mobile?: boolean;
  onEntitySelect?: OnEntitySelectCallback;
  handleEntitySelect(entity: IEntityDetails): void;
  openAppSelectionDialog(): void;
  handleClose(): void;
}
export const EntitySwitcherDialog: React.FC<IEntitySwitcherDialogProps> = ({
  open,
  activeEntity,
  selectedEntity,
  entitledEntities,
  forcedDefaultApplication,
  openAppSelectionDialog,
  handleEntitySelect,
  mobile,
  onEntitySelect,
  handleClose
}) => {
  const firstFourInactiveEntities = getInactiveEntities({ entitledEntities, activeEntity, limit: 4 });
  const onClose = () => {
    handleClose();
    setTimeout(() => handleEntitySelect(null), 500);
  };
  return (
    <Dialog
      fullScreen={mobile}
      data-test={
        mobile
          ? DATA_TEST.entity_switcher_selection_dialog_container_mobile
          : DATA_TEST.entity_switcher_dialog_container
      }
      onClose={onClose}
      open={open}
      data-og={DATA_OG.entity_switcher_dialog_container}
    >
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <DialogTitle variant="h2">Switch Entities</DialogTitle>
        <IconButton
          sx={{ height: '40px', width: '40px', marginRight: '20px' }}
          aria-label="close"
          onClick={handleClose}
          data-test={DATA_TEST.entity_switcher_cancel_icon}
          data-og={DATA_OG.entity_switcher_cancel_icon}
        >
          <SvgIcon inheritViewBox component={CloseIcon} />
        </IconButton>
      </Box>
      <DialogContent>
        <DialogContentText fontWeight={500}>Select one to continue</DialogContentText>
        <Box sx={{ display: 'flex', flexDirection: mobile ? 'column' : 'row' }}>
          <EntityOptionButton
            mobile={mobile}
            selected={selectedEntity?.id === activeEntity?.id}
            handleClick={() => handleEntitySelect(activeEntity)}
            name={activeEntity?.displayName || activeEntity?.name}
            dataTest={DATA_TEST.initial_active_entity_button}
          />
          {firstFourInactiveEntities?.map((entity) => {
            return (
              <EntityOptionButton
                mobile={mobile}
                key={entity.id}
                selected={selectedEntity?.id === entity?.id}
                handleClick={() => handleEntitySelect(entity)}
                name={entity?.displayName || entity?.name}
                dataTest={getDynamicDataTestId(DATA_TEST.entity_chip, entity.name)}
              />
            );
          })}
        </Box>
        <Divider sx={{ color: 'text.secondary', marginTop: '8px', padding: '0px 8px' }}>
          <Typography sx={{ color: 'text.secondary' }}>OR</Typography>
        </Divider>
        <DesktopEntityAutoCompleteSearch
          displayEntityNamesOnly={mobile}
          selectedEntity={selectedEntity}
          entitledEntities={entitledEntities}
          handleEntitySelect={handleEntitySelect}
        />
      </DialogContent>
      <DialogActions>
        <Button
          data-test={DATA_TEST.entity_switcher_selection_dialog_cancel_button}
          data-og={DATA_OG.entity_switcher_selection_dialog_cancel_button}
          variant="text"
          color="secondary"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          data-test={DATA_TEST.entity_switcher_selection_dialog_continue_button}
          data-og={DATA_OG.entity_switcher_selection_dialog_continue_button}
          variant="contained"
          disabled={!selectedEntity}
          onClick={() => {
            const url = forcedDefaultApplication
              ? getApplicationByKey(selectedEntity, forcedDefaultApplication)?.url
              : getEntityDefaultApplication(selectedEntity)?.url;
            if (url) {
              navigateToEntityUrl(
                onEntitySelect,
                selectedEntity,
                url,
                (finalUrl) => window.open(finalUrl, '_self'),
                onClose
              );
            } else {
              openAppSelectionDialog();
              handleClose();
            }
          }}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};
